<template>
  <div>
    <div
      v-for="(fabric, index) in formData"
      :key="`fabric-${index}`"
      class="fabric-box elevation-1"
      :class="{ 'red-border': failComposition(fabric) }">
      <v-row>
        <v-col
          cols="12"
          class="d-flex justify-space-between">
          <p
            class="mt-1"
            :class="{ 'red--text': failComposition(fabric) }">
            {{ `Fabric #${index + 1}` }}
            <span v-if="fabric.status === 'deleted'">
              ผ้านี้ถูกลบแล้วกรุณาเลือกใหม่
            </span>
            <span v-else-if="failComposition(fabric) && fabric.id">
              ( ยังไม่ระบุ compositions )
            </span>
          </p>
          <div>
            <v-btn
              v-if="failComposition(fabric) && fabric.id && fabric.status !== 'deleted'"
              color="secondary"
              class="mr-2"
              small
              target="_blank"
              @click="selectFabric(index, fabric.id)">
              ระบุ compositions
            </v-btn>
            <v-btn
              color="secondary"
              class="mr-2"
              small
              @click="selectFabric(index)">
              <v-icon class="mr-1">
                mdi-tshirt-crew
              </v-icon>
              เลือกผ้า
            </v-btn>
            <v-btn
              v-if="fabric.id || formData.length > 1"
              color="error"
              class="mr-2"
              icon
              @click="deleteFabric(index)">
              <v-icon>
                mdi-delete
              </v-icon>
            </v-btn>
          </div>
        </v-col>
        <v-col
          v-if="fabric.id"
          cols="12"
          md="6">
          <v-row class="ma-0">
            <div
              v-for="(img, imgIndex) in fabric.imageUrls"
              :key="`draggableImg-${imgIndex}`"
              class="col col-auto">
              <v-img
                v-if="img"
                :aspect-ratio="1"
                :src="$convertImgix(`${img}`)"
                :width="150"
                class="elevation-1"
                alt="img">
              </v-img>
            </div>
          </v-row>
        </v-col>
        <v-col
          v-if="fabric.id"
          cols="12"
          class="d-flex flex-wrap pa-0"
          md="6">
          <v-col
            v-if="requiredComposition"
            md="12"
            sm="12"
            cols="12">
            <v-checkbox
              v-model="fabric.printInFabricLabel"
              color="secondary"
              class="mt-0"
              hide-details
              label="Show in fabric label"
            >
              เลือกผ้า
            </v-checkbox>
          </v-col>
          <v-col
            md="6"
            sm="6"
            cols="12">
            <v-text-field
              v-model.trim="fabric.hangId"
              :disabled="isOnlyPreview"
              label="Hang Id*"
              class="uppercase"
              hide-details
              outlined
              dense
              :rules="isFormValid(index)" />
          </v-col>
          <v-col
            md="6"
            sm="6"
            cols="12">
            <v-text-field
              v-model.trim="fabric.fabricNo"
              :disabled="isOnlyPreview"
              label="Fabric Id*"
              class="uppercase"
              hide-details
              outlined
              dense
              :rules="isFormValid(index)" />
          </v-col>
          <v-col
            md="6"
            sm="6"
            cols="12">
            <v-combobox
              v-model.trim="fabric.factory"
              :disabled="isOnlyPreview"
              :items="factory"
              label="Factory*"
              outlined
              deletable-chips
              small-chips
              dense
              hide-details
              :rules="isFormValid(index)"
              class="uppercase" />
          </v-col>
          <v-col
            md="6"
            sm="6"
            cols="12">
            <v-text-field
              v-model.trim="fabric.size"
              label="Size"
              class="uppercase"
              hide-details
              outlined
              dense />
          </v-col>
          <v-col
            md="6"
            sm="6"
            cols="12">
            <v-text-field
              v-model.trim="fabric.color"
              label="Color"
              class="uppercase"
              hide-details
              outlined
              dense />
          </v-col>
          <v-col
            v-if="!isSketch"
            md="6"
            sm="6"
            cols="12">
            <v-text-field
              v-model.number="fabric.amount"
              type="number"
              label="Amount"
              hide-details
              outlined
              :min="0"
              dense
              onwheel="this.blur()"
              @wheel="$event.target.blur()" />
          </v-col>
          <v-col
            md="6"
            sm="6"
            cols="12">
            <v-text-field
              v-model.number="fabric.unitPrice"
              type="number"
              label="Unit Price"
              hide-details
              outlined
              :min="0"
              dense
              onwheel="this.blur()"
              @wheel="$event.target.blur()" />
          </v-col>
          <v-col
            md="6"
            sm="6"
            cols="12">
            <v-select
              v-model="fabric.currency"
              :items="currencyOptions"
              :menu-props="{ offsetY: true }"
              label="Currency"
              item-text="label"
              item-value="value"
              outlined
              dense
              hide-details />
          </v-col>
          <v-col
            md="12"
            sm="12"
            cols="12">
            <v-select
              v-model="fabric['garmentPart']"
              item-text="th"
              return-object
              label="Garment part"
              dense
              outlined
              :rules="isGarmentValid(index)"
              :items="garmentParts">
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ item.en }} / {{ item.th }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <span>{{ item.en }} / {{ item.th }}</span>
              </template>
            </v-select>
          </v-col>
        </v-col>
        <v-col
          v-if="fabric.id"
          cols="12">
          <v-textarea
            v-model.trim="fabric.note"
            label="Note"
            dense
            hide-details
            outlined />
        </v-col>
      </v-row>
      <v-row v-if="fabric.id">
        <v-col cols="12 d-flex justify-space-between">
          <p class="mt-2">
            Patterns
          </p>
          <v-btn
            color="secondary"
            small
            @click="addMorePattern(index)">
            <v-icon class="mr-1">
              mdi-plus
            </v-icon>
            เพิ่ม Pattern
          </v-btn>
        </v-col>
      </v-row>
      <v-row
        v-for="(pattern, indexPattern) in fabric.patterns"
        :key="`pattern-${indexPattern}`">
        <v-col
          v-if="fabric.id"
          cols="12"
          md="6">
          <gw-drop-file
            v-model="pattern.imageUrls"
            :multiple="true"
            need-compress
            class="fabric-drop-file"
            s3-path="/sketch-images"
            caption="คลิกเพื่อเพิ่มรูปภาพ"
            :limit="10"
            label-bold />
        </v-col>
        <v-col
          v-if="fabric.id"
          cols="12"
          md="6">
          <v-textarea
            v-model.trim="pattern.note"
            label="Note"
            dense
            hide-details
            outlined />
          <v-btn
            v-if="fabric.patterns.length > 1"
            color="error"
            class="mt-4 delete-fabric-btn mr-2"
            @click="deletePattern(index, indexPattern)">
            <v-icon>
              mdi-delete
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <p
      v-if="hasWhole"
      class="mt-6 orange--text">
      เนื่องจากมี Fabric ที่เลือก Garment part เป็น Whole อยู่จึงไม่สามารถเพิ่ม Fabric อื่นอีกได้ <br>
      หากต้องการเพิ่ม Fabric โปรดแก้ไข Garment
    </p>
    <v-btn
      class="my-4"
      :disabled="hasWhole"
      style="width: 100%;"
      color="primary"
      @click="addMoreFabric()">
      <v-icon left>
        mdi-plus
      </v-icon>
      เพิ่มผ้า
    </v-btn>
    <fabric-list-modal
      v-if="dialog"
      v-model.trim="dialog"
      :update-fabric-id="updateFabricId"
      @on-select="onSelected($event)"
      @on-close="closeModal()" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import FabricsFactoryProvider from '@/resources/FabricsFactoyProvider'
import FabricListModal from './FabricListModal.vue'
import GarmentParts from '@/assets/json/GarmentParts.json'

const FabricsFactoryService = new FabricsFactoryProvider()

export default {
  components: { FabricListModal },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    showAmount: {
      type: Boolean,
      default: false
    },
    isSketch: {
      type: Boolean,
      default: false
    },
    formValidates: {
      type: Array,
      default: () => []
    },
    requiredComposition: {
      type: Boolean,
      default: false
    },
    onFillComposition: {
      type: Function,
      required: false,
      default: () => null
    }
  },
  data () {
    return {
      selectedIndex: -1,
      dialog: false,
      currencyOptions: [
        {
          label: 'บาท',
          value: 'thb'
        },
        {
          label: 'หยวน',
          value: 'cny'
        },
        {
          label: 'ดอลลาร์',
          value: 'usd'
        }
      ],
      garmentParts: GarmentParts,
      factoryData: [],
      isOnlyPreview: true,
      updateFabricId: '',
      isNewFabric: false
    }
  },
  computed: {
    formData: {
      get () {
        this.value.forEach((v) => {
          if (!Array.isArray(v.compositions)) {
            this.$set(v, 'compositions', [])
          }
          if (typeof v.printInFabricLabel !== 'boolean') {
            this.$set(v, 'printInFabricLabel', true)
          }
        })
        return this.value
      },
      set (newVal) {
        this.$emit('input', newVal)
      }
    },
    factory: {
      get () {
        const results = this.factoryData.map((item) => item.name)
        return results
      },
      set (newVal) {
        const newValue = newVal[0] ? newVal[0].trim().toLowerCase() : ''
        this.formData.factory = newValue
      }
    },
    duplicatedGarments () {
      const counts = {}
      this.formData.forEach(({ garmentPart }) => {
        const key = garmentPart?.en
        if (key) counts[key] = (counts[key] || 0) + 1
      })
      // if validate duplicate more add key to validateList
      const validateList = ['Body']
      return Object.keys(counts).filter((k) => (counts[k] > 1) && validateList.includes(k))
    },
    hasWhole () {
      return this.formData.findIndex((el) => el?.garmentPart?.en === 'Whole') !== -1
    }
  },
  created () {
    this.getFabricFactory()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setLoading: 'Components/setLoading'
    }),
    selectFabric (index, fabricId = null) {
      if (fabricId) this.updateFabricId = fabricId
      this.selectedIndex = index
      this.dialog = true
    },
    onSelected (fabric) {
      const _fabric = JSON.parse(JSON.stringify(fabric))
      let payload = JSON.parse(JSON.stringify(this.formData))
       if (this.isNewFabric) {
        payload.push({
          sketchId: '',
          hangId: _fabric.hangId,
          fabricNo: _fabric.fabricNo,
          id: _fabric.id,
          imageUrls: _fabric.imageUrls,
          factory: _fabric.factory,
          size: _fabric.size,
          color: _fabric.color,
          note: '',
          collectionName: '',
          amount: 0,
          currency: _fabric.currency,
          unitPrice: _fabric.unitPrice || 0,
          patterns: [{
            note: '',
            imageUrls: []
          }],
          garmentPart: null,
          printInFabricLabel: true
        })
      } else if (this.formData[this.selectedIndex].id !== _fabric.id && !this.isNewFabric) {
        payload[this.selectedIndex] = {
          sketchId: this.formData[this.selectedIndex]?.sketchId || '',
          hangId: _fabric.hangId,
          fabricNo: _fabric.fabricNo,
          id: _fabric.id,
          imageUrls: _fabric.imageUrls,
          factory: _fabric.factory,
          size: _fabric.size,
          color: _fabric.color,
          note: this.formData[this.selectedIndex]?.note || '',
          collectionName: this.formData[this.selectedIndex]?.collectionName || '',
          amount: 0,
          currency: _fabric.currency,
          unitPrice: _fabric.unitPrice || 0,
          patterns: this.formData[this.selectedIndex]?.patterns || _fabric.patterns || [],
          garmentPart: this.formData[this.selectedIndex]?.garmentPart || null,
          printInFabricLabel: this.formData[this.selectedIndex]?.printInFabricLabel || true
        }
      }
      payload = payload.map((p) => {
        const newPayload = { ...p }
        if (p.id === _fabric.id) {
          newPayload['compositions'] = _fabric.compositions || []
        }
        return newPayload
      })
      this.formData = [...payload]
      this.$emit('input', payload)
      this.$emit('onFillComposition', _fabric)

      this.closeModal()
    },
    closeModal () {
      this.isNewFabric = false
      this.selectedIndex = -1
      this.updateFabricId = ''
      this.dialog = false
    },
    addMoreFabric () {
      this.isNewFabric = true
      this.dialog = true
    },
    addMorePattern (index) {
      const tmpData = [...this.formData]

      tmpData[index].patterns.push({
        imageUrls: [],
        note: ''
      })

      this.formData = tmpData
    },
    deleteFabric (index) {
      if (this.formData.length === 1) {
        this.formData[0] = {
          hangId: '',
          fabricNo: '',
          imageUrls: [],
          factory: '',
          size: '',
          color: '',
          note: '',
          collectionName: '',
          amount: 0,
          currency: 'thb',
          unitPrice: 0,
          patterns: [{
            note: '',
            imageUrls: []
          }],
          garmentPart: null,
          compositions: [],
          printInFabricLabel: true
        }
      } else {
        this.formData.splice(index, 1)
      }
      this.$emit('input', [...this.formData])
    },
    deletePattern (index, patternIndex) {
      const tmpData = [...this.formData]

      tmpData[index].patterns.splice(patternIndex, 1)

      this.formData = tmpData
    },
    async getFabricFactory () {
      try {
        const { data } = await FabricsFactoryService.getAll({ limit: 9999 })
        this.factoryData = data.results
      } catch (error) {
        console.error('getFabricFactory', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      }
    },
    isFormValid (index) {
      if (this.formValidates[index]) {
        return []
      }
      return [
        (v) => !!v || 'Field is require!'
      ]
    },
    isGarmentValid (index) {
      if (this.formValidates[index]) {
        return []
      }
      return [
        (v) => !!v || 'Field is required!',
        () => {
          if (this.hasWhole && this.formData.length > 1) {
            return 'การเลือก Garment part เป็น Whole จะมี Fabric ได้เพียง 1 แบบเท่านั้น'
          }
          return true
        },
        (v) => !this.isDuplicateGarment(v) || 'Garment part are duplicated.'
      ]
    },
    isDuplicateGarment (garment) {
      if (!garment || !garment.en) return false
      return this.duplicatedGarments.includes(garment.en)
    },
    failComposition (fabric) {
      return this.requiredComposition && Array.isArray(fabric.compositions) && fabric.compositions.length === 0 && fabric.printInFabricLabel
    }
  }
}
</script>

<style>
.fabric-box {
  border: 1px solid #a3a3a3;
  padding: 16px;
  margin-top: 16px;
  border-radius: 6px;
}
.qty-field {
  width: 100%;
}

.fabric-drop-file .upload-file {
  height: 258px;
}

.delete-fabric-btn.v-btn {
  height: 40px !important;
}

.field-label {
  font-weight: bold;
}

.fabric-amount-input .v-messages__message {
  margin-top: 5px;
  font-size: 15px;
}
.uppercase input {
  text-transform: uppercase;
}
.red-border {
  border-color: red;
}
</style>
